/**
 * M10.3: first-time entry — found a civilization in the shared galaxy.
 * Three short steps (identity → starting area → confirm), every choice validated again by the
 * server. A full galaxy is an explicit, retryable state; the account stays valid.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CIV_COLORS, CIV_EMBLEMS, CivNameSchema,
  type FoundingStatus, type FoundingOffer, type Civilization, type StarType,
} from '@galaxis/shared';
import { fetchFoundingStatus, foundCivilization } from '../../api/founding.ts';
import { ApiError } from '../../api/client.ts';
import { APP_NAME } from '../../brand.ts';
import { AccountView } from '../AccountView.tsx';
import { STAR_TYPE_LABELS, STAR_TYPE_COLORS } from '../SystemPanel.tsx';
import { EmblemBadge, Emblem } from './Emblem.tsx';

type Step = 'identity' | 'home' | 'review';
type Choose = Extract<FoundingStatus, { state: 'choose' }>;

const SHIP_LABELS: Record<string, string> = { scout: 'kutatóhajó', construction: 'építőhajó', corvette: 'korvett', frigate: 'fregatt', cruiser: 'cirkáló' };
const ISOLATION_TEXT: Record<FoundingOffer['isolation'], string> = {
  'távoli': 'Távol minden birodalomtól',
  'közepes': 'Biztonságos távolságban',
  'közeli': 'Birodalmak közelében',
};
const fmt = (n: number) => n.toLocaleString('hu-HU');
const errText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

function newKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function FoundingScreen({ onFounded }: { onFounded: (civ: Civilization) => void }) {
  const [status, setStatus] = useState<FoundingStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showAccount, setShowAccount] = useState(false);
  const [step, setStep] = useState<Step>('identity');
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [color, setColor] = useState<string>(CIV_COLORS[5].id);
  const [emblem, setEmblem] = useState<string>(CIV_EMBLEMS[0].id);
  const [systemId, setSystemId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // One key per founding attempt: a retried request after a network error is not a second civilization
  const idempotencyKey = useRef(newKey());

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const s = await fetchFoundingStatus();
      setStatus(s);
      if (s.state === 'founded') onFounded(s.civilization);
      if (s.state === 'choose') setSystemId(cur => (cur && s.offers.some(o => o.systemId === cur) ? cur : null));
    } catch (e) { setLoadError(errText(e, 'A galaxis adatai nem tölthetők be.')); }
  }, [onFounded]);
  useEffect(() => { void load(); }, [load]);

  const colorLabel = CIV_COLORS.find(c => c.id === color)?.label ?? '';
  const emblemLabel = CIV_EMBLEMS.find(e => e.id === emblem)?.label ?? '';
  const choose = status?.state === 'choose' ? status : null;
  const chosen = choose?.offers.find(o => o.systemId === systemId) ?? null;

  const toHome = () => {
    const parsed = CivNameSchema.safeParse(name);
    if (!parsed.success) { setNameError(parsed.error.errors[0]?.message ?? 'Érvénytelen név'); return; }
    setName(parsed.data);
    setNameError(null);
    setStep('home');
  };

  const submit = async () => {
    if (!choose || !chosen) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await foundCivilization({ name, color, emblem, offerId: choose.offerId, systemId: chosen.systemId, idempotencyKey: idempotencyKey.current });
      onFounded(res.civilization);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined;
      if (code === 'name_taken') {
        setNameError(errText(e, 'A név foglalt.'));
        setStep('identity');
      } else if (code === 'home_unavailable' || code === 'offer_expired' || code === 'offer_invalid' || code === 'galaxy_full') {
        setNotice(errText(e, 'A kezdőterület már nem elérhető.'));
        setSystemId(null);
        setStep('home');
        await load();
      } else if (code === 'already_founded') {
        await load();
      } else {
        setSubmitError(errText(e, 'Az alapítás nem sikerült. Próbáld újra.'));
      }
      // A different choice is a different request
      if (code && code !== 'already_founded') idempotencyKey.current = newKey();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full h-full overflow-y-auto bg-space-900" style={{ touchAction: 'pan-y' }} data-testid="founding-screen">
      <header className="sci-fi-panel border-b border-slate-700/50 flex items-center gap-2 px-3 sm:px-4 h-12 sticky top-0 z-10">
        <span className="font-mono font-semibold text-sm tracking-widest text-accent truncate uppercase">{APP_NAME}</span>
        <span className="flex-1" />
        {/* Account settings and sign-out live in "Fiókom", like in the game */}
        <button type="button" className="btn-secondary" onClick={() => setShowAccount(v => !v)}>
          {showAccount ? 'Vissza az alapításhoz' : 'Fiókom'}
        </button>
      </header>

      {showAccount ? <div className="flex flex-col min-h-[calc(100%-3rem)]"><AccountView /></div> : (
        <main className="max-w-4xl mx-auto px-4 py-6 sm:py-8 flex flex-col gap-5">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-100">Alapítsd meg civilizációdat</h1>
            <p className="text-sm text-slate-400 mt-1">
              Egy fiókhoz egy civilizáció tartozik a közös, tartós galaxisban{status && status.state !== 'founded' ? <> (<span data-testid="world-name">{status.worldName}</span>)</> : null}.
            </p>
          </div>

          {loadError && (
            <div className="notice notice-error flex flex-col sm:flex-row sm:items-center gap-2" role="alert">
              <span className="flex-1">{loadError}</span>
              <button type="button" className="btn-secondary" onClick={() => void load()}>Újra</button>
            </div>
          )}
          {!status && !loadError && <p className="text-sm text-slate-400" role="status">A galaxis feltérképezése…</p>}

          {status?.state === 'galaxy_full' && (
            <section className="sci-fi-panel rounded-xl p-5 flex flex-col gap-3" data-testid="galaxy-full">
              <h2 className="text-lg font-semibold text-slate-100">Jelenleg nincs szabad kezdőterület</h2>
              <p className="text-sm text-slate-300">{status.message}</p>
              <p className="text-xs text-slate-500">Nem hozunk létre birodalmat szabálytalan vagy más által birtokolt helyen. Amint felszabadul vagy bővül a galaxis, itt folytathatod.</p>
              <button type="button" className="btn-primary sm:max-w-xs" onClick={() => void load()}>Újra ellenőrzöm</button>
            </section>
          )}

          {choose && (
            <>
              <ol className="grid grid-cols-3 gap-2 text-xs" aria-label="Lépések">
                {(['identity', 'home', 'review'] as const).map((s, i) => (
                  <li key={s} aria-current={step === s ? 'step' : undefined}
                    className="rounded-lg px-2 py-2 text-center"
                    style={{ border: `1px solid ${step === s ? 'rgba(74,158,255,0.6)' : 'rgba(148,163,184,0.2)'}`, color: step === s ? '#93c5fd' : '#94a3b8' }}>
                    {i + 1}. {s === 'identity' ? 'Identitás' : s === 'home' ? 'Kezdőterület' : 'Megerősítés'}
                  </li>
                ))}
              </ol>

              <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5 items-start">
                <div className="flex flex-col gap-4 min-w-0">
                  {step === 'identity' && (
                    <section className="sci-fi-panel rounded-xl p-4 sm:p-5 flex flex-col gap-4" aria-label="Identitás">
                      <div>
                        <label htmlFor="civ-name" className="field-label">Civilizáció neve</label>
                        <input id="civ-name" className="field-input" value={name} maxLength={40} autoComplete="off"
                          onChange={e => { setName(e.target.value); setNameError(null); }}
                          aria-invalid={nameError ? 'true' : undefined} aria-describedby="civ-name-help" />
                        <p id="civ-name-help" className="text-xs text-slate-500 mt-1">3–32 karakter; betű, szám, szóköz, kötőjel. Egyedinek kell lennie a galaxisban.</p>
                        {nameError && <p className="field-error" role="alert">{nameError}</p>}
                      </div>
                      <fieldset>
                        <legend className="field-label">Szín</legend>
                        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Szín">
                          {CIV_COLORS.map(c => (
                            <button key={c.id} type="button" role="radio" aria-checked={color === c.id} aria-label={c.label} title={c.label}
                              onClick={() => setColor(c.id)}
                              className="rounded-full flex items-center justify-center"
                              style={{ width: 44, height: 44, background: c.id, outline: color === c.id ? '3px solid #e2e8f0' : 'none', outlineOffset: 2 }} />
                          ))}
                        </div>
                      </fieldset>
                      <fieldset>
                        <legend className="field-label">Embléma</legend>
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" role="radiogroup" aria-label="Embléma">
                          {CIV_EMBLEMS.map(e => (
                            <button key={e.id} type="button" role="radio" aria-checked={emblem === e.id} aria-label={e.label} title={e.label}
                              onClick={() => setEmblem(e.id)}
                              className="rounded-lg flex flex-col items-center justify-center gap-0.5 py-1"
                              style={{ minHeight: 56, border: `1px solid ${emblem === e.id ? color : 'rgba(148,163,184,0.25)'}`, background: emblem === e.id ? `${color}1f` : 'transparent' }}>
                              <Emblem id={e.id} color={emblem === e.id ? color : '#94a3b8'} size={24} />
                              <span className="text-[10px] text-slate-400">{e.label}</span>
                            </button>
                          ))}
                        </div>
                      </fieldset>
                      <button type="button" className="btn-primary" onClick={toHome}>Tovább a kezdőterülethez</button>
                    </section>
                  )}

                  {step === 'home' && (
                    <section className="sci-fi-panel rounded-xl p-4 sm:p-5 flex flex-col gap-3" aria-label="Kezdőterület">
                      <div className="flex items-center gap-2">
                        <h2 className="text-sm font-semibold text-slate-200 flex-1">Válassz kezdőterületet</h2>
                        <button type="button" className="btn-secondary" onClick={() => { setNotice(null); void load(); }}>Frissítés</button>
                      </div>
                      <p className="text-xs text-slate-400">A szerver által ellenőrzött, szabad és biztonságos rendszerek. A végleges foglaláskor újra ellenőrizzük.</p>
                      {notice && <div className="notice notice-info" role="status">{notice}</div>}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Kezdőterületek">
                        {choose.offers.map(o => (
                          <OfferCard key={o.systemId} offer={o} selected={o.systemId === systemId} color={color} onSelect={() => setSystemId(o.systemId)} />
                        ))}
                      </div>
                      <div className="flex flex-col-reverse sm:flex-row gap-2">
                        <button type="button" className="btn-secondary" onClick={() => setStep('identity')}>Vissza</button>
                        <button type="button" className="btn-primary sm:flex-1" disabled={!chosen} onClick={() => setStep('review')}>Tovább a megerősítéshez</button>
                      </div>
                    </section>
                  )}

                  {step === 'review' && chosen && (
                    <section className="sci-fi-panel rounded-xl p-4 sm:p-5 flex flex-col gap-4" aria-label="Megerősítés">
                      <h2 className="text-sm font-semibold text-slate-200">Indulás előtt</h2>
                      <StartingSummary pkg={choose.startingPackage} home={chosen} />
                      {submitError && <div className="notice notice-error" role="alert">{submitError}</div>}
                      <div className="flex flex-col-reverse sm:flex-row gap-2">
                        <button type="button" className="btn-secondary" onClick={() => setStep('home')} disabled={submitting}>Vissza</button>
                        <button type="button" className="btn-primary sm:flex-1" onClick={() => void submit()} disabled={submitting}>
                          {submitting ? 'Alapítás…' : 'Civilizáció megalapítása'}
                        </button>
                      </div>
                    </section>
                  )}
                </div>

                <aside className="sci-fi-panel rounded-xl p-4 flex items-center lg:flex-col lg:items-start gap-3 lg:sticky lg:top-16" aria-label="Előnézet" data-testid="civ-preview">
                  <EmblemBadge id={emblem} color={color} size={56} title={emblemLabel} />
                  <div className="min-w-0">
                    <div className="text-base font-semibold break-words" style={{ color }}>{name.trim() || 'Névtelen civilizáció'}</div>
                    <div className="text-xs text-slate-400">{colorLabel} · {emblemLabel}</div>
                    {chosen && <div className="text-xs text-slate-300 mt-1">Anyarendszer: {chosen.name}</div>}
                  </div>
                </aside>
              </div>
            </>
          )}
        </main>
      )}
    </div>
  );
}

function OfferCard({ offer, selected, color, onSelect }: { offer: FoundingOffer; selected: boolean; color: string; onSelect: () => void }) {
  const star = offer.starType as StarType;
  return (
    <button type="button" role="radio" aria-checked={selected} onClick={onSelect} data-testid="home-offer"
      className="text-left rounded-xl p-3 flex flex-col gap-2 transition-colors"
      style={{ minHeight: 44, border: `2px solid ${selected ? color : 'rgba(148,163,184,0.2)'}`, background: selected ? `${color}14` : 'rgba(15,23,42,0.6)' }}>
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: STAR_TYPE_COLORS[star] ?? '#e2e8f0' }} aria-hidden />
        <span className="font-semibold text-slate-100 truncate" data-testid="offer-name">{offer.name}</span>
      </div>
      <div className="text-xs text-slate-400">{STAR_TYPE_LABELS[star] ?? offer.starType} · {offer.region}</div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <dt className="text-slate-500">Lakhatóság</dt><dd className="text-slate-200">{offer.habitability}</dd>
        <dt className="text-slate-500">Fémlelőhely</dt><dd className="text-slate-200">{fmt(offer.metalPerHour)}/óra</dd>
        <dt className="text-slate-500">Energialelőhely</dt><dd className="text-slate-200">{fmt(offer.energyPerHour)}/óra</dd>
        <dt className="text-slate-500">Útvonalak</dt><dd className="text-slate-200">{offer.laneCount}</dd>
      </dl>
      <div className="text-xs" style={{ color: offer.isolation === 'közeli' ? '#fcd34d' : '#86efac' }}>{ISOLATION_TEXT[offer.isolation]}</div>
    </button>
  );
}

function StartingSummary({ pkg, home }: { pkg: Choose['startingPackage']; home: FoundingOffer }) {
  const ships = useMemo(() => pkg.ships.map(s => `${s.count} ${SHIP_LABELS[s.class] ?? s.class}`).join(', '), [pkg.ships]);
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-lg p-3" style={{ border: '1px solid rgba(148,163,184,0.2)' }}>
          <div className="text-xs text-slate-400 mb-1">Anyarendszer</div>
          <div className="text-slate-100 font-medium">{home.name}</div>
          <div className="text-xs text-slate-400">{home.region}</div>
        </div>
        <div className="rounded-lg p-3" style={{ border: '1px solid rgba(148,163,184,0.2)' }} data-testid="starting-resources">
          <div className="text-xs text-slate-400 mb-1">Induló készlet</div>
          <div className="text-slate-100">{fmt(pkg.metal)} fém · {fmt(pkg.energy)} energia · {fmt(pkg.credits)} kredit</div>
          <div className="text-xs text-slate-400">Anyarendszer-termelés: {fmt(pkg.homeIncomePerHour.metal)} fém és {fmt(pkg.homeIncomePerHour.energy)} energia óránként</div>
        </div>
      </div>
      <div className="rounded-lg p-3" style={{ border: '1px solid rgba(148,163,184,0.2)' }}>
        <div className="text-xs text-slate-400 mb-1">Induló flotta</div>
        <div className="text-slate-100">{ships}</div>
      </div>
      <div className="notice notice-info" data-testid="protection-rules">
        <strong>{pkg.protectionHours} órás kezdővédelem:</strong> ennyi ideig a rendszereid és flottáid nem támadhatók, és a gépi birodalmak sem üzennek neked hadat.
        Ha közben te támadsz meg valakit, a védelem azonnal megszűnik.
      </div>
    </div>
  );
}
